import React, { useState, useEffect, useCallback } from 'react';

/* Standalone-safe apiFetch (same pattern as Admin.jsx) */
async function apiFetch(url, options = {}) {
  const token = localStorage.getItem('bizsuite_token');
  const headers = { ...(options.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (!options.headers?.['Content-Type'] && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(url, { ...options, headers, credentials: 'include' });
  if (res.status === 401) {
    localStorage.removeItem('bizsuite_token');
    window.location.href = '/login.html';
    throw new Error('Unauthorized');
  }
  return res;
}

const MODULE_ORDER = [
  'System',
  'Company',
  'CRM',
  'HCM',
  'Contracts',
  'Projects',
  'Billing & Revenue',
  'Finance',
  'Procurement',
  'ESS',
  'Rate Cards & Estimates',
];

const MODULE_ICONS = {
  'System': '\u2699\uFE0F',
  'Company': '\uD83C\uDFE2',
  'CRM': '\uD83D\uDCBC',
  'HCM': '\uD83D\uDC65',
  'Contracts': '\uD83D\uDCC4',
  'Projects': '\uD83D\uDCCA',
  'Billing & Revenue': '\uD83D\uDCB0',
  'Finance': '\uD83C\uDFE6',
  'Procurement': '\uD83D\uDED2',
  'ESS': '\uD83D\uDE4B',
  'Rate Cards & Estimates': '\uD83D\uDCCB',
};

const styles = {
  container: {
    padding: '24px 0',
  },
  header: {
    marginBottom: 24,
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: '#E2E8F0',
    margin: 0,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 13,
    color: '#64748B',
    margin: 0,
  },
  moduleSection: {
    marginBottom: 20,
    background: 'rgba(30,41,59,0.7)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 12,
    overflow: 'hidden',
  },
  moduleSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 20px',
    cursor: 'pointer',
    userSelect: 'none',
    background: 'rgba(30,41,59,0.9)',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  moduleSectionHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  moduleIcon: {
    fontSize: 18,
  },
  moduleName: {
    fontSize: 15,
    fontWeight: 700,
    color: '#E2E8F0',
  },
  moduleCount: {
    fontSize: 11,
    color: '#64748B',
    background: 'rgba(100,116,139,0.15)',
    padding: '2px 8px',
    borderRadius: 10,
    fontWeight: 500,
  },
  protectedNote: {
    fontSize: 10,
    color: '#F59E0B',
    background: 'rgba(245,158,11,0.1)',
    padding: '2px 8px',
    borderRadius: 4,
    fontWeight: 600,
  },
  chevron: {
    fontSize: 14,
    color: '#64748B',
    transition: 'transform 0.2s',
  },
  entityList: {
    padding: '12px 16px',
  },
  entityRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderRadius: 8,
    background: 'rgba(30,41,59,0.5)',
    border: '1px solid rgba(255,255,255,0.06)',
    marginBottom: 8,
  },
  entityRowLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  entityLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: '#E2E8F0',
  },
  protectedBadge: {
    fontSize: 10,
    color: '#F59E0B',
    background: 'rgba(245,158,11,0.1)',
    padding: '2px 6px',
    borderRadius: 4,
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  },
  recordCount: {
    fontSize: 12,
    color: '#64748B',
  },
  entityRowRight: {
    display: 'flex',
    gap: 8,
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  modal: {
    background: 'rgba(30,41,59,0.98)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 16,
    padding: '28px 32px',
    minWidth: 440,
    maxWidth: 520,
    boxShadow: '0 24px 48px rgba(0,0,0,0.4)',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: '#E2E8F0',
    marginBottom: 4,
    margin: 0,
  },
  modalSubtitle: {
    fontSize: 13,
    color: '#64748B',
    marginBottom: 20,
    margin: 0,
    marginTop: 4,
  },
  fileInput: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(15,23,42,0.8)',
    color: '#E2E8F0',
    fontSize: 13,
    marginBottom: 16,
    outline: 'none',
    boxSizing: 'border-box',
  },
  radioGroup: {
    marginBottom: 16,
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 14px',
    borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.06)',
    background: 'rgba(15,23,42,0.5)',
    marginBottom: 8,
    cursor: 'pointer',
    fontSize: 13,
    color: '#E2E8F0',
  },
  radioLabelDisabled: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 14px',
    borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.04)',
    background: 'rgba(15,23,42,0.3)',
    marginBottom: 8,
    cursor: 'not-allowed',
    fontSize: 13,
    color: '#475569',
  },
  radioLabelSelected: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 14px',
    borderRadius: 8,
    border: '1px solid rgba(96,165,250,0.4)',
    background: 'rgba(96,165,250,0.08)',
    marginBottom: 8,
    cursor: 'pointer',
    fontSize: 13,
    color: '#E2E8F0',
  },
  warningBox: {
    background: 'rgba(239,68,68,0.08)',
    border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: 8,
    padding: '10px 14px',
    marginBottom: 16,
    fontSize: 12,
    color: '#FCA5A5',
    lineHeight: 1.5,
  },
  warningLabel: {
    fontWeight: 700,
    color: '#EF4444',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 8,
  },
  resultBox: {
    marginTop: 16,
    padding: '12px 14px',
    borderRadius: 8,
    fontSize: 13,
    lineHeight: 1.5,
  },
  resultSuccess: {
    background: 'rgba(34,197,94,0.08)',
    border: '1px solid rgba(34,197,94,0.3)',
    color: '#86EFAC',
  },
  resultError: {
    background: 'rgba(239,68,68,0.08)',
    border: '1px solid rgba(239,68,68,0.3)',
    color: '#FCA5A5',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 60,
    color: '#64748B',
    fontSize: 14,
  },
  errorContainer: {
    padding: 24,
    textAlign: 'center',
    color: '#FCA5A5',
    fontSize: 14,
  },
};

export default function DataManagement() {
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collapsedModules, setCollapsedModules] = useState({});
  const [uploadModal, setUploadModal] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadMode, setUploadMode] = useState('append');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [downloadingKey, setDownloadingKey] = useState(null);

  const fetchEntities = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiFetch('/api/admin/data-management/entities');
      if (!res.ok) throw new Error('Failed to load entities');
      const data = await res.json();
      setEntities(Array.isArray(data) ? data : data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEntities();
  }, [fetchEntities]);

  const groupedEntities = {};
  for (const entity of entities) {
    const mod = entity.module || 'Other';
    if (!groupedEntities[mod]) groupedEntities[mod] = [];
    groupedEntities[mod].push(entity);
  }

  const orderedModules = MODULE_ORDER.filter(m => groupedEntities[m]);
  const extraModules = Object.keys(groupedEntities).filter(m => !MODULE_ORDER.includes(m));
  const allModules = [...orderedModules, ...extraModules];

  function toggleModule(mod) {
    setCollapsedModules(prev => ({ ...prev, [mod]: !prev[mod] }));
  }

  async function handleDownload(entityKey, label) {
    setDownloadingKey(entityKey);
    try {
      const res = await fetch(`/api/admin/data-management/download/${entityKey}`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('bizsuite_token')}` },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${label.replace(/\s+/g, '_')}_data.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Download failed: ' + err.message);
    } finally {
      setDownloadingKey(null);
    }
  }

  function openUploadModal(entity) {
    setUploadModal(entity);
    setUploadFile(null);
    setUploadMode('append');
    setUploading(false);
    setUploadResult(null);
  }

  function closeUploadModal() {
    setUploadModal(null);
    setUploadFile(null);
    setUploadMode('append');
    setUploading(false);
    setUploadResult(null);
  }

  async function handleUpload() {
    if (!uploadFile || !uploadModal) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      const res = await fetch(
        `/api/admin/data-management/upload/${uploadModal.key}?mode=${uploadMode}`,
        {
          method: 'POST',
          body: formData,
          credentials: 'include',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('bizsuite_token')}` },
        }
      );
      const result = await res.json();
      if (!res.ok) {
        setUploadResult({ success: false, message: result.error || result.message || 'Upload failed' });
      } else {
        setUploadResult({
          success: true,
          message: result.message || `Successfully ${uploadMode === 'append' ? 'appended' : 'replaced'} records for ${uploadModal.label}.`,
          details: result,
        });
        fetchEntities();
      }
    } catch (err) {
      setUploadResult({ success: false, message: 'Upload failed: ' + err.message });
    } finally {
      setUploading(false);
    }
  }

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        Loading data entities...
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <p>Failed to load entities: {error}</p>
        <button className="btn btn-primary btn-sm" onClick={fetchEntities} style={{ marginTop: 12 }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Data Management</h2>
        <p style={styles.subtitle}>
          Download and upload data for all entities in the system. Protected entities support append-only uploads.
        </p>
      </div>

      {allModules.map(mod => {
        const moduleEntities = groupedEntities[mod];
        const isCollapsed = !!collapsedModules[mod];
        const isSystem = mod === 'System';
        const icon = MODULE_ICONS[mod] || '\uD83D\uDCC1';

        return (
          <div key={mod} style={styles.moduleSection}>
            <div
              style={styles.moduleSectionHeader}
              onClick={() => toggleModule(mod)}
            >
              <div style={styles.moduleSectionHeaderLeft}>
                <span style={styles.moduleIcon}>{icon}</span>
                <span style={styles.moduleName}>{mod}</span>
                <span style={styles.moduleCount}>
                  {moduleEntities.length} {moduleEntities.length === 1 ? 'entity' : 'entities'}
                </span>
                {isSystem && (
                  <span style={styles.protectedNote}>
                    Admin — Protected
                  </span>
                )}
              </div>
              <span style={{ ...styles.chevron, transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}>
                &#9660;
              </span>
            </div>

            {!isCollapsed && (
              <div style={styles.entityList}>
                {moduleEntities.map(entity => (
                  <div key={entity.key} style={styles.entityRow}>
                    <div style={styles.entityRowLeft}>
                      <span style={styles.entityLabel}>{entity.label}</span>
                      {entity.protected && (
                        <span style={styles.protectedBadge}>
                          \uD83D\uDD12 Protected
                        </span>
                      )}
                      <span style={styles.recordCount}>
                        {entity.count != null ? entity.count.toLocaleString() : '—'} records
                      </span>
                    </div>
                    <div style={styles.entityRowRight}>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleDownload(entity.key, entity.label)}
                        disabled={downloadingKey === entity.key}
                      >
                        {downloadingKey === entity.key ? 'Downloading...' : 'Download XLSX'}
                      </button>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => openUploadModal(entity)}
                      >
                        Upload XLSX
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {allModules.length === 0 && !loading && (
        <div style={styles.loadingContainer}>
          No entities found.
        </div>
      )}

      {uploadModal && (
        <div style={styles.overlay} onClick={(e) => { if (e.target === e.currentTarget) closeUploadModal(); }}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Upload Data — {uploadModal.label}</h3>
            <p style={styles.modalSubtitle}>
              Upload an XLSX file to import records into {uploadModal.label}.
              {uploadModal.protected && ' This entity is protected — append only, cannot overwrite.'}
            </p>

            <input
              type="file"
              accept=".xlsx,.xls"
              style={styles.fileInput}
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              disabled={uploading}
            />

            <div style={styles.radioGroup}>
              <label
                style={uploadMode === 'append' ? styles.radioLabelSelected : styles.radioLabel}
                onClick={() => { if (!uploading) setUploadMode('append'); }}
              >
                <input
                  type="radio"
                  name="uploadMode"
                  value="append"
                  checked={uploadMode === 'append'}
                  onChange={() => setUploadMode('append')}
                  disabled={uploading}
                />
                <div>
                  <div style={{ fontWeight: 600 }}>Append new records</div>
                  <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>
                    Add uploaded records to existing data. Existing records are not modified.
                  </div>
                </div>
              </label>

              <label
                style={
                  uploadModal.protected
                    ? styles.radioLabelDisabled
                    : uploadMode === 'overwrite'
                      ? styles.radioLabelSelected
                      : styles.radioLabel
                }
                onClick={() => {
                  if (!uploading && !uploadModal.protected) setUploadMode('overwrite');
                }}
              >
                <input
                  type="radio"
                  name="uploadMode"
                  value="overwrite"
                  checked={uploadMode === 'overwrite'}
                  onChange={() => { if (!uploadModal.protected) setUploadMode('overwrite'); }}
                  disabled={uploading || uploadModal.protected}
                />
                <div>
                  <div style={{ fontWeight: 600 }}>
                    Overwrite all data
                    {uploadModal.protected && (
                      <span style={{ fontSize: 10, color: '#F59E0B', marginLeft: 8 }}>
                        \uD83D\uDD12 Disabled for protected entities
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: uploadModal.protected ? '#475569' : '#94A3B8', marginTop: 2 }}>
                    Replace all existing records with the uploaded data.
                  </div>
                </div>
              </label>
            </div>

            {uploadMode === 'overwrite' && !uploadModal.protected && (
              <div style={styles.warningBox}>
                <span style={styles.warningLabel}>WARNING: </span>
                This will DELETE all existing {uploadModal.label} records and replace them with the uploaded data. This action cannot be undone.
              </div>
            )}

            {uploadResult && (
              <div
                style={{
                  ...styles.resultBox,
                  ...(uploadResult.success ? styles.resultSuccess : styles.resultError),
                }}
              >
                {uploadResult.success ? '\u2705 ' : '\u274C '}
                {uploadResult.message}
                {uploadResult.details?.inserted != null && (
                  <div style={{ marginTop: 6, fontSize: 12 }}>
                    Inserted: {uploadResult.details.inserted}
                    {uploadResult.details.updated != null && ` | Updated: ${uploadResult.details.updated}`}
                    {uploadResult.details.skipped != null && ` | Skipped: ${uploadResult.details.skipped}`}
                    {uploadResult.details.errors != null && uploadResult.details.errors > 0 && (
                      <span style={{ color: '#FCA5A5' }}> | Errors: {uploadResult.details.errors}</span>
                    )}
                  </div>
                )}
              </div>
            )}

            <div style={styles.modalActions}>
              <button
                className="btn btn-secondary btn-sm"
                onClick={closeUploadModal}
                disabled={uploading}
              >
                {uploadResult?.success ? 'Close' : 'Cancel'}
              </button>
              {!uploadResult?.success && (
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleUpload}
                  disabled={!uploadFile || uploading}
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
